import './layout.css';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { api } from '../lib/api-client.js';
import { useComparisonCount } from '../hooks.js';
import { Button, TextField } from './ui.js';
import { ElectionNotice } from './election-notice.js';

const primaryLinks = [
  { to: '/apuracao_eleicoes_2026_segundo_turno', label: 'Apuração do 2º turno', live: true },
  { to: '/politicos', label: 'Políticos' },
  { to: '/partidos', label: 'Partidos' },
  { to: '/candidaturas', label: 'Candidaturas' },
  { to: '/presidentes', label: 'Presidentes' },
  { to: '/impostometro', label: 'Impostômetro' },
  { to: '/comparar', label: 'Comparar' },
  { to: '/fontes', label: 'Fontes' },
];

/** Keep page actions in their original position on desktop and inside the
 * existing hamburger menu at the same breakpoint used by the site header. */
export function MobileMenuActions({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 900px)');
    const sync = () => setTarget(mobile.matches ? document.getElementById('mobile-page-actions') : null);
    sync();
    mobile.addEventListener('change', sync);
    return () => mobile.removeEventListener('change', sync);
  }, []);
  return target ? createPortal(children, target) : children;
}

export function ThemeToggle() {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem('vitrine-theme') === 'dark';
    setDark(saved);
    document.documentElement.dataset.theme = saved ? 'dark' : 'light';
  }, []);
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.dataset.theme = next ? 'dark' : 'light';
    localStorage.setItem('vitrine-theme', next ? 'dark' : 'light');
  };
  return <Button variant="unstyled" className="theme-toggle" type="button" role="switch" aria-checked={dark} aria-label={dark ? 'Ativar tema claro' : 'Ativar tema escuro'} onClick={toggle}>
    <span className="theme-toggle-icon theme-toggle-icon-sun" aria-hidden="true">☼</span><span className="theme-toggle-icon theme-toggle-icon-moon" aria-hidden="true">☾</span><span className="theme-toggle-thumb" />
  </Button>;
}

function ActiveVisitors() {
  const [active, setActive] = useState<number | null>(null);
  useEffect(() => {
    let sessionId: string;
    try { sessionId = sessionStorage.getItem('vitrine-presence') ?? crypto.randomUUID(); }
    catch { sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`; }
    try { sessionStorage.setItem('vitrine-presence', sessionId); } catch { /* A presença funciona sem persistência local. */ }
    let disposed = false;
    const heartbeat = async () => {
      try {
        const result = await api<{ active: number }>('/api/metrics/public/active', { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });
        if (!disposed) setActive(result.active);
      } catch { if (!disposed) setActive(null); }
    };
    void heartbeat();
    const interval = window.setInterval(heartbeat, 30_000);
    const leave = () => { void api('/api/metrics/public/leave', { method: 'POST', body: JSON.stringify({ session_id: sessionId }), keepalive: true }).catch(() => undefined); };
    window.addEventListener('pagehide', leave);
    window.addEventListener('pageshow', heartbeat);
    return () => { disposed = true; window.clearInterval(interval); window.removeEventListener('pagehide', leave); window.removeEventListener('pageshow', heartbeat); };
  }, []);
  if (active === null) return null;
  return <div className="active-visitors" role="status" aria-live="polite"><span className="active-visitors-indicator" /><strong>{new Intl.NumberFormat('pt-BR').format(active)}</strong><span className="active-visitors-label">visitantes agora</span></div>;
}

export function SiteLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const comparisonCount = useComparisonCount(location.pathname, location.search);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('q') ?? '');
  useEffect(() => {
    setMenuOpen(false);
    void api('/api/metrics/public/view', { method: 'POST', keepalive: true }).catch(() => undefined);
  }, [location.pathname]);
  useEffect(() => setSearch(new URLSearchParams(location.search).get('q') ?? ''), [location.search]);

  return <>
    <a className="skip" href="#main">Ir para o conteúdo</a>
    <header className="site-header">
      <div className="header-inner">
        <Link className="brand" to="/apuracao_eleicoes_2026_segundo_turno" aria-label="Vitrine de Político, início">
          <svg className="brand-building" viewBox="0 0 40 40" aria-hidden="true"><path d="M5 13 20 5l15 8H5Zm3 3h4v13H8zm10 0h4v13h-4zm10 0h4v13h-4zM5 32h30v4H5z" /></svg>
          <span>Vitrine de Político<span className="brand-small">Informação que aproxima</span></span>
        </Link>
        <div className="header-actions">
          <Button variant="unstyled" className="header-icon-button" type="button" aria-label={searchOpen ? 'Fechar busca' : 'Abrir busca'} aria-expanded={searchOpen} onClick={() => setSearchOpen(!searchOpen)}><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.5" /><path d="m16 16 5 5" /></svg></Button>
          <Button variant="unstyled" className="nav-toggle" type="button" aria-controls="main-nav" aria-expanded={menuOpen} aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'} onClick={() => setMenuOpen(!menuOpen)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg></Button>
        </div>
        <nav id="main-nav" className={menuOpen ? 'is-open' : ''} aria-label="Principal">
          {primaryLinks.map((item) => <NavLink key={item.to} className={({ isActive }) => `${item.live ? 'live-nav-link ' : ''}${isActive ? 'active' : ''}`} to={item.to}>
            {item.live && <span className="live-nav-dot" aria-hidden="true" />}{item.label}{item.to === '/comparar' && comparisonCount > 0 && <span className="comparison-nav-count" aria-label={`${comparisonCount} políticos selecionados`}>({comparisonCount})</span>}
          </NavLink>)}
          <div id="mobile-page-actions" role="group" aria-label="Ações da apuração" />
          <ThemeToggle />
        </nav>
        <form className={`site-search ${searchOpen ? 'is-open' : ''}`} action="/politicos" method="get">
          <label className="visually-hidden" htmlFor="site-search-input">Buscar políticos ou partidos</label>
          <input id="site-search-input" name="q" type="search" placeholder="Buscar político, partido ou estado…" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Button variant="unstyled" type="submit" aria-label="Pesquisar"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.5" /><path d="m16 16 5 5" /></svg></Button>
        </form>
      </div>
    </header>
    <main id="main" tabIndex={-1}>{children}</main>
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-brand">
          <svg className="footer-brand-mark" viewBox="0 0 40 40" aria-hidden="true"><path d="M5 13 20 5l15 8H5Zm3 3h4v13H8zm10 0h4v13h-4zm10 0h4v13h-4zM5 32h30v4H5z" /></svg>
          <span><strong>Vitrine de Político</strong><small>Desenvolvido com recursos próprios</small></span>
        </div>
        <a className="footer-instagram" href="https://www.instagram.com/pericles.radaelli/" target="_blank" rel="noopener noreferrer" aria-label="Instagram de Pericles Radaelli, abre em nova aba">
          <strong className="footer-credit-title">Desenvolvido por</strong>
          <span className="footer-instagram-row"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle className="footer-instagram-dot" cx="17.5" cy="6.5" r=".8" /></svg><small>Instagram <u>@pericles.radaelli</u></small><span className="footer-instagram-arrow" aria-hidden="true">↗</span></span>
        </a>
      </div>
      <div className="footer-secondary">
        <div className="footer-notes"><p>Dados de fontes oficiais · Cobertura indicada por período. Contagem de atividades não mede qualidade ou impacto.</p><p>Registramos apenas totais diários de visualizações, sem identificar visitantes.</p></div>
        <div className="footer-bottom">
          <nav className="footer-links" aria-label="Links do rodapé"><Link to="/fontes">Fontes oficiais</Link><Link to="/metodologia">Metodologia</Link><Link to="/privacidade">Privacidade e publicidade</Link><Button className="footer-suggestions-trigger" type="button" onClick={() => setSuggestionOpen(true)}>Enviar sugestão</Button></nav>
          <p className="footer-copyright">© 2026 Vitrine de Político. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
    <SuggestionDialog open={suggestionOpen} onOpenChange={setSuggestionOpen} />
    <ElectionNotice />
    <ActiveVisitors />
  </>;
}

function SuggestionDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (open) return;
    setName(''); setCity(''); setComment(''); setError(''); setSubmitted(false); setPending(false);
  }, [open]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(''); setPending(true);
    try {
      await api('/api/suggestions', { method: 'POST', body: JSON.stringify({ name, city, comment }) });
      setSubmitted(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Não foi possível enviar a sugestão. Tente novamente.');
    } finally { setPending(false); }
  };

  return <dialog ref={dialogRef} className="suggestion-dialog" aria-labelledby="suggestion-dialog-title" aria-describedby="suggestion-dialog-description" onCancel={(event) => { if (pending) event.preventDefault(); }} onClose={() => onOpenChange(false)} onClick={(event) => { if (!pending && event.target === event.currentTarget) onOpenChange(false); }}>
    <section className="suggestion-dialog-card">
      <header className="suggestion-dialog-heading">
        <div className="suggestion-dialog-copy">
          <span className="suggestion-dialog-eyebrow">Contribua com a Vitrine</span>
          <h2 id="suggestion-dialog-title">Envie sua sugestão</h2>
          <p id="suggestion-dialog-description">Sua opinião ajuda a tornar as informações mais úteis e claras.</p>
        </div>
        <Button variant="unstyled" className="suggestion-dialog-close" type="button" aria-label="Fechar formulário de sugestão" disabled={pending} onClick={() => onOpenChange(false)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </Button>
      </header>
      {submitted ? <div className="suggestion-success" role="status" aria-live="polite"><strong>Sugestão enviada.</strong><p>Obrigado por contribuir com a Vitrine de Político.</p><Button variant="primary" type="button" onClick={() => onOpenChange(false)}>Concluir</Button></div> : <form className="suggestion-form" onSubmit={submit}>
        <TextField label="Nome" name="name" autoComplete="name" maxLength={100} value={name} onValueChange={setName} required />
        <TextField label="Cidade" name="city" autoComplete="address-level2" maxLength={120} value={city} onValueChange={setCity} required />
        <label className="form-field">Comentário<textarea name="comment" value={comment} onChange={(event) => setComment(event.target.value)} maxLength={3000} rows={5} required /></label>
        <p className="suggestion-privacy-note">Nome, cidade e comentário serão armazenados e acessíveis apenas à equipe pelo painel administrativo.</p>
        {error && <p className="notice error" role="alert">{error}</p>}
        <div className="suggestion-form-actions"><Button type="button" disabled={pending} onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" variant="primary" disabled={pending}>{pending ? 'Enviando…' : 'Enviar sugestão'}</Button></div>
      </form>}
    </section>
  </dialog>;
}

